/**
 * Tenant workspace shell — sticky header (name + chips + actions) + tab strip
 * + Outlet for the active tab.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useEffect, useState } from 'react';
import {
  Link as RouterLink,
  NavLink,
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
  useParams,
  useRevalidator,
} from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import type { BrandDto, ProductDto, TenantDto } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusChip } from '@/components/StatusChip';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';
import { getBrandsByProduct, getProducts } from '@/api/lookupCache';
import { ApiError } from '@/api/errors';
import { useCurrentOperator } from '@/auth/AuthContext';

export interface TenantWorkspaceLoaderData {
  tenant: TenantDto;
  products: ProductDto[];
  brand: BrandDto | null;
}

export async function tenantWorkspaceLoader({
  params,
}: {
  params: { id?: string };
}): Promise<TenantWorkspaceLoaderData> {
  const id = Number(params.id);
  const [tenant, products] = await Promise.all([
    apiAdmin.tenants.get(id),
    getProducts().catch(() => [] as ProductDto[]),
  ]);
  let brand: BrandDto | null = null;
  if (tenant.brandId) {
    brand = await apiAdmin.brands.get(tenant.brandId).catch(() => null);
  }
  // Pre-warm brand cache for the product
  if (tenant.productId) {
    void getBrandsByProduct(tenant.productId).catch(() => null);
  }
  return { tenant, products, brand };
}

interface TabSpec {
  value: string;
  label: string;
  fallback: string;
  superAdmin?: boolean;
  externalHidden?: boolean; // hidden when the tenant's product authMode is EXTERNAL (SASADM-0070)
}

const TABS: readonly TabSpec[] = [
  { value: 'overview', label: 'tenants.tabs.overview', fallback: 'Overview' },
  { value: 'whitelabel', label: 'tenants.tabs.whitelabel', fallback: 'Whitelabel' },
  { value: 'branding', label: 'tenants.tabs.branding', fallback: 'Branding' },
  { value: 'members', label: 'tenants.tabs.members', fallback: 'Members', externalHidden: true },
  {
    value: 'invitations',
    label: 'tenants.tabs.invitations',
    fallback: 'Invitations',
    externalHidden: true,
  },
  {
    value: 'email-templates',
    label: 'tenants.tabs.emailTemplates',
    fallback: 'Email templates',
    externalHidden: true,
  },
  {
    value: 'integration-settings',
    label: 'tenants.tabs.integrationSettings',
    fallback: 'Integration Settings',
    superAdmin: true,
  },
  { value: 'settings', label: 'tenants.tabs.settings', fallback: 'Settings' },
  // SASADM-0076 — "Connected Apps (OIDC)" = OUTBOUND: which downstream apps this
  // tenant's members can sign into with their Journix identity. externalHidden:
  // external-auth tenants are self-contained (no saas-auth members to grant).
  {
    value: 'oidc',
    label: 'tenants.tabs.oidc',
    fallback: 'Connected Apps (OIDC)',
    superAdmin: true,
    externalHidden: true,
  },
  // SASADM-0075 — per-tenant Microsoft Entra SSO config (super-admin only).
  // SASADM-0076 — "Microsoft SSO" = INBOUND login; externalHidden because an
  // external tenant's users don't sign in through saas-auth (config is inert).
  {
    value: 'microsoft-sso',
    label: 'tenants.tabs.microsoftSso',
    fallback: 'Microsoft SSO',
    superAdmin: true,
    externalHidden: true,
  },
];

export function TenantWorkspace() {
  const { tenant, products, brand } = useLoaderData() as TenantWorkspaceLoaderData;
  const { t } = useCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const params = useParams<{ id: string }>();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirm, setConfirm] = useState<{
    title: string;
    body: string;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const product = tenant.productId ? products.find((p) => p.id === tenant.productId) : null;
  // SASADM-0070 — external-auth tenants authenticate via their own SSO, so the
  // saas-auth account surfaces (Members / Invitations / Email templates tabs and
  // the signup-mode chip/field) don't apply and are hidden.
  const isExternal = product?.authMode === 'EXTERNAL';
  const isPlatformTenant = tenant.slug === 'saas-admin';

  const activeTab =
    TABS.find((tb) => location.pathname.endsWith(`/${tb.value}`))?.value ?? 'overview';

  // SASADM-0070 — hiding a tab from the strip doesn't stop a direct navigation to
  // its route; send external tenants off a hidden tab to Overview so it's unreachable.
  useEffect(() => {
    const spec = TABS.find((tb) => tb.value === activeTab);
    if (isExternal && spec?.externalHidden) {
      navigate(`/tenants/${params.id}/overview`, { replace: true });
    }
  }, [isExternal, activeTab, params.id, navigate]);

  async function setStatus(next: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED', i18nKey: string) {
    try {
      await apiAdmin.tenants.setStatus(tenant.id, next);
      snack.success(t('snackbar.statusUpdated', 'Status updated.'));
      revalidator.revalidate();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? t(`errors.codes.${err.code}`, err.details.message ?? err.code)
          : t('errors.generic', 'Something went wrong.');
      snack.error(msg);
    }
    setConfirm(null);
    void i18nKey;
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.tenants', 'Tenants'), to: '/tenants' },
          { label: tenant.name },
        ]}
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{
          alignItems: { md: 'center' },
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h1" component="h1">
            {tenant.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {tenant.slug}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <StatusChip kind="tenantStatus" value={tenant.status} />
          {/* SASADM-0070 — signup-mode chip hidden for external-auth tenants. */}
          {!isExternal ? <StatusChip kind="tenantSignupMode" value={tenant.signupMode} /> : null}
          {product ? <Chip label={product.displayName} color="info" size="small" /> : null}
          {brand ? (
            <Chip label={brand.displayName} color="secondary" size="small" />
          ) : (
            <Chip
              label={t('tenants.overview.inheritsBrand', 'Inherits product default')}
              size="small"
            />
          )}
          <Button
            variant="outlined"
            size="small"
            endIcon={<MoreVertIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            {t('common.actions', 'Actions')}
          </Button>
          <Menu
            open={Boolean(menuAnchor)}
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            {tenant.status !== 'SUSPENDED' && !isPlatformTenant ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setConfirm({
                    title: t('tenants.confirm.suspendTitle', 'Suspend tenant?'),
                    body: t('tenants.confirm.suspendBody', '', { name: tenant.name }),
                    onConfirm: () => setStatus('SUSPENDED', 'snackbar.statusUpdated'),
                  });
                }}
              >
                {t('tenants.actions.suspend', 'Suspend')}
              </MenuItem>
            ) : null}
            {tenant.status !== 'ACTIVE' && !isPlatformTenant ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setConfirm({
                    title: t('tenants.confirm.restoreTitle', 'Restore tenant?'),
                    body: t('tenants.confirm.restoreBody', '', { name: tenant.name }),
                    onConfirm: () => setStatus('ACTIVE', 'snackbar.statusUpdated'),
                  });
                }}
              >
                {t('tenants.actions.restore', 'Restore')}
              </MenuItem>
            ) : null}
            {tenant.status !== 'INACTIVE' && !isPlatformTenant ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setConfirm({
                    title: t('tenants.confirm.inactivateTitle', 'Inactivate tenant?'),
                    body: t('tenants.confirm.inactivateBody', '', { name: tenant.name }),
                    onConfirm: () => setStatus('INACTIVE', 'snackbar.statusUpdated'),
                  });
                }}
              >
                {t('tenants.actions.inactivate', 'Inactivate')}
              </MenuItem>
            ) : null}
            {!isPlatformTenant ? (
              <MenuItem
                component={RouterLink}
                to={`/workflows/tenants/${tenant.id}/reassign-product`}
                onClick={() => setMenuAnchor(null)}
              >
                {t('tenants.actions.reassignProduct', 'Reassign product')}
              </MenuItem>
            ) : null}
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                navigate(`/tenants/${tenant.id}/settings`);
              }}
            >
              {t('tenants.actions.editSettings', 'Edit settings')}
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
      {!tenant.productId ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('tenants.overview.warnings.noProduct', '')}
        </Alert>
      ) : null}
      <Tabs
        value={activeTab}
        onChange={(_e, v) => navigate(`/tenants/${params.id}/${v}`)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.filter(
          (tb) => (!tb.superAdmin || operator?.isSuperAdmin) && !(isExternal && tb.externalHidden),
        ).map((tb) => (
          <Tab
            key={tb.value}
            value={tb.value}
            label={t(tb.label, tb.fallback)}
            component={NavLink}
            to={`/tenants/${params.id}/${tb.value}`}
          />
        ))}
      </Tabs>
      <Outlet />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmLabel={t('common.yes', 'Yes')}
        cancelLabel={t('common.cancel', 'Cancel')}
        onConfirm={() => void confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
}
